/**
 * Place and address lookup.
 *
 * Uses Photon, an OpenStreetMap geocoder built for typeahead: it is keyless,
 * CORS-enabled and tolerant of partial input, which matters when the field is
 * being typed into. Nominatim rejects requests that cannot set a User-Agent,
 * which rules it out of a browser.
 */

import { getJson, type RequestOptions } from "@viamissa/http";

const API = "https://photon.komoot.io/api/";

export const ATTRIBUTION = "Geocoding by Photon / OpenStreetMap contributors";

export type Place = {
  /** Short name, e.g. "Blessed Sacrament Church". */
  name: string;
  /** Full single-line description for display. */
  label: string;
  lat: number;
  lng: number;
};

export type GeocodeQuery = {
  query: string;
  limit?: number;
  /** Bias results towards this point — usually the map or the last known area. */
  near?: { lat: number; lng: number };
  /** Two-letter language code for returned names. */
  language?: string;
  http?: RequestOptions;
};

type Feature = {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, unknown>;
};

const str = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * County name, given the suffix Photon leaves off unevenly — "King" for one
 * result and "Lewis County" for the next. Bare, it reads as a town.
 */
function toCounty(properties: Record<string, unknown>): string | null {
  const value = str(properties.county);
  if (!value) return null;
  return /\bcounty$/i.test(value) ? value : `${value} County`;
}

/**
 * Build a readable one-line label from Photon's address parts.
 *
 * Wild features rarely carry a city, and the same name repeats across a state —
 * Washington has a Snow Lake in King County and another in Lewis. Falling back
 * to the county is what makes those two rows tell themselves apart.
 */
function toLabel(properties: Record<string, unknown>): string {
  const name = str(properties.name);
  const house = str(properties.housenumber);
  const street = str(properties.street);
  const address = [house, street].filter(Boolean).join(" ");
  const area = str(properties.city) ?? toCounty(properties);

  return (
    [name, address || null, area, str(properties.state)]
      .filter((part, index, all) => part && all.indexOf(part) === index)
      .join(", ") || (name ?? "Unknown place")
  );
}

/** Search for places matching free-form text. Returns an empty list on failure. */
export async function geocode(query: GeocodeQuery): Promise<Place[]> {
  const { query: text, limit = 5, near, language = "en", http } = query;
  if (text.trim().length < 3) return [];

  const params = new URLSearchParams({ q: text, limit: String(limit), lang: language });
  if (near) {
    params.set("lat", String(near.lat));
    params.set("lon", String(near.lng));
  }

  try {
    const data = await getJson<{ features?: Feature[] }>(`${API}?${params}`, http);

    const places: Place[] = [];
    for (const feature of data.features ?? []) {
      const coordinates = feature.geometry?.coordinates;
      const properties = feature.properties ?? {};
      const lng = coordinates?.[0];
      const lat = coordinates?.[1];
      if (typeof lat !== "number" || typeof lng !== "number") continue;

      places.push({
        name: str(properties.name) ?? toLabel(properties),
        label: toLabel(properties),
        lat,
        lng,
      });
    }
    return places;
  } catch {
    return [];
  }
}

/** First match for a query, or `null`. */
export async function geocodeFirst(query: GeocodeQuery): Promise<Place | null> {
  const [first] = await geocode({ ...query, limit: 1 });
  return first ?? null;
}
